import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ConfirmModal from '@/components/seop/ConfirmModal';
import EmptyState from '@/components/seop/EmptyState';
import { useToast } from '@/contexts/ToastContext';
import { useSeopMonth } from '@/hooks/useSeopMonth';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { seopService } from '@/services/seop.service';
import type {
  SeopCapacity,
  SeopCapacityInput,
  SeopLever,
  SeopMonth,
  SeopPhysicalResource,
  SeopResource,
} from '@/types/seop';
import { fmtHours, fmtUtil, parseHours } from '@/utils/seopFormat';

const CELL = 'py-2 pr-3 text-sm text-content-primary';
const INPUT = 'w-24 rounded-lg border border-theme-border bg-surface-secondary px-2 py-1 text-sm text-right';

/**
 * Disponibilidade herdada quando o mês de origem não tem valor.
 *
 * **0,8, não 0 nem 1.** É o `Number(r.availability?.[source]) || .8` do
 * `insertMonthlyData`. Um recurso que herdasse 0 ficaria com capacidade zero e
 * sumiria do plano em silêncio — por isso o valor aplicado aparece na tela.
 */
const DISPONIBILIDADE_PADRAO = 0.8;

/** `shiftValues()`: os três fixos + nomes de alavanca que não são gasto nem terceirização. */
function turnosDisponiveis(levers: SeopLever[]): string[] {
  const daAlavanca = levers
    .map((x) => String(x.lever || ''))
    .filter((x) => x && !x.startsWith('GASTOS') && x !== 'TERCEIRIZAÇÃO');
  return [...new Set(['', 'CONVENCIONAL', '6X2', '12X36', ...daAlavanca])];
}

/** As cinco deduções, na ordem da tela do Herrison. Rótulo curto: a coluna é estreita. */
const DEDUCOES = [
  { campo: 'maintenance_hours', titulo: 'Manutenção' },
  { campo: 'validation_hours', titulo: 'Validação' },
  { campo: 'qualification_hours', titulo: 'Qualificação' },
  { campo: 'research_hours', titulo: 'P&D' },
  { campo: 'other_hours', titulo: 'Outros' },
] as const;

type CampoDeducao = (typeof DEDUCOES)[number]['campo'];

interface Rascunho {
  calendar_days?: number;
  shift_hours?: number;
  shift_count?: number;
  oee?: number;
  flex_hours?: number;
  shift?: string;
  maintenance_hours?: number;
  validation_hours?: number;
  qualification_hours?: number;
  research_hours?: number;
  other_hours?: number;
}

interface Efetivo {
  dias: number;
  horasTurno: number;
  turnos: number;
  oee: number;
  bruta: number;
  aposOee: number;
  deducoes: Record<CampoDeducao, number>;
  totalDeducoes: number;
  extra: number;
  capacidade: number;
  shift: string;
}

/**
 * Valor em vigor = rascunho não salvo sobre o que veio do banco.
 *
 * Modelo PROCESSO SOP: `dias do calendário × horas de turno × turnos × OEE`. A base
 * legada em horas saiu da conta — ela continua no banco como memória do cadastro.
 * Dias nulo significa automático, e é por isso que a tela lê `effective_calendar_days`:
 * a regra da janela 16→15 mora no backend e só lá.
 */
function primeiro(...candidatos: (number | null | undefined)[]): number {
  return candidatos.find((x) => x !== null && x !== undefined) ?? 0;
}

function calcularEfetivo(c: SeopCapacity | undefined, d: Rascunho): Efetivo {
  const dias = primeiro(d.calendar_days, c?.calendar_days, c?.effective_calendar_days);
  const horasTurno = primeiro(d.shift_hours, c?.shift_hours, 8);
  const turnos = primeiro(d.shift_count, c?.shift_count, 3);
  const oee = primeiro(d.oee, c?.oee, c?.availability, 1);
  const extra = primeiro(d.flex_hours, c?.flex_hours);
  const bruta = dias * horasTurno * turnos;
  const aposOee = bruta * oee;
  const deducoes = Object.fromEntries(
    DEDUCOES.map(({ campo }) => [campo, primeiro(d[campo], c?.[campo])]),
  ) as Record<CampoDeducao, number>;
  const totalDeducoes = Object.values(deducoes).reduce((a, b) => a + b, 0);
  return {
    dias,
    horasTurno,
    turnos,
    oee,
    bruta,
    aposOee,
    deducoes,
    totalDeducoes,
    extra,
    // Grampeada em zero como no motor: dedução maior que a capacidade é recurso sem
    // hora nenhuma, não capacidade negativa.
    capacidade: Math.max(0, aposOee - totalDeducoes),
    shift: d.shift ?? c?.shift ?? '',
  };
}

const LinhaCapacidade: FC<{
  resource: SeopResource;
  efetivo: Efetivo;
  turnos: string[];
  onEdit: (campo: keyof Rascunho, valor: number | string) => void;
}> = ({ resource: r, efetivo: e, turnos, onEdit }) => {
  const semCapacidade = e.capacidade === 0;
  return (
    <tr
      data-testid={`seop-cap-${r.name}`}
      className={`border-b border-theme-border/50 ${
        semCapacidade ? 'bg-theme-danger/5' : 'hover:bg-surface-hover/50'
      }`}
    >
      <td className={`${CELL} text-content-secondary`}>{r.critical}</td>
      <td className={`${CELL} font-semibold`}>
        {r.name}
        {semCapacidade && (
          <span
            data-testid={`seop-cap-zero-${r.name}`}
            className="ml-2 rounded-full border border-theme-danger/40 bg-theme-danger/10 px-2 py-0.5 text-xs text-theme-danger"
          >
            capacidade zero
          </span>
        )}
      </td>
      <td className={`${CELL} text-right`}>
        <input
          type="number"
          min={0}
          step={1}
          aria-label={`Dias do calendário ${r.name}`}
          defaultValue={e.dias}
          onBlur={(ev) => onEdit('calendar_days', Math.max(0, Number(ev.target.value) || 0))}
          className={INPUT}
        />
      </td>
      <td className={`${CELL} text-right`}>
        <input
          aria-label={`Horas por turno ${r.name}`}
          defaultValue={fmtHours(e.horasTurno)}
          onBlur={(ev) => onEdit('shift_hours', parseHours(ev.target.value))}
          className={INPUT}
        />
      </td>
      <td className={`${CELL} text-right`}>
        <input
          type="number"
          min={0}
          step={1}
          aria-label={`Turnos por dia ${r.name}`}
          defaultValue={e.turnos}
          onBlur={(ev) => onEdit('shift_count', Math.max(0, Number(ev.target.value) || 0))}
          className={INPUT}
        />
      </td>
      <td className={`${CELL} text-right`}>
        <input
          type="number"
          min={0}
          max={100}
          step={0.1}
          aria-label={`OEE ${r.name}`}
          defaultValue={Math.round(e.oee * 1000) / 10}
          onBlur={(ev) => onEdit('oee', Math.max(0, Math.min(100, Number(ev.target.value) || 0)) / 100)}
          className={INPUT}
        />
      </td>
      <td className={`${CELL} text-right tabular-nums text-content-secondary`}>{fmtHours(e.aposOee)} h</td>
      {DEDUCOES.map(({ campo, titulo }) => (
        <td key={campo} className={`${CELL} text-right`}>
          <input
            aria-label={`${titulo} ${r.name}`}
            defaultValue={fmtHours(e.deducoes[campo])}
            onBlur={(ev) => onEdit(campo, parseHours(ev.target.value))}
            className={INPUT}
          />
        </td>
      ))}
      <td className={`${CELL} text-right`}>
        <input
          aria-label={`Horas extra ${r.name}`}
          defaultValue={fmtHours(e.extra)}
          onBlur={(ev) => onEdit('flex_hours', parseHours(ev.target.value))}
          className={INPUT}
        />
      </td>
      <td className={CELL}>
        <select
          aria-label={`Turno ${r.name}`}
          value={e.shift}
          onChange={(ev) => onEdit('shift', ev.target.value)}
          className="rounded-lg border border-theme-border bg-surface-secondary px-2 py-1 text-sm"
        >
          {turnos.map((t) => (
            <option key={t || '—'} value={t}>
              {t || '— sem turno —'}
            </option>
          ))}
        </select>
      </td>
      <td className={`${CELL} text-right font-semibold tabular-nums`}>{fmtHours(e.capacidade)} h</td>
    </tr>
  );
};

const TabelaCapacidade: FC<{
  resources: SeopResource[];
  efetivo: (r: SeopResource) => Efetivo;
  turnos: string[];
  onEdit: (id: string, campo: keyof Rascunho, valor: number | string) => void;
}> = ({ resources, efetivo, turnos, onEdit }) => {
  if (resources.length === 0) {
    return (
      <EmptyState
        icon="factory"
        title="Nenhum recurso cadastrado"
        hint="Cadastre os recursos de balanceamento em Cadastros para lançar capacidade aqui."
      />
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
            <th className="pb-2 pr-3 text-left">Área crítica</th>
            <th className="pb-2 pr-3 text-left">Recurso</th>
            <th className="pb-2 pr-3 text-right">Dias do calendário</th>
            <th className="pb-2 pr-3 text-right">Horas turno</th>
            <th className="pb-2 pr-3 text-right">Turnos</th>
            <th className="pb-2 pr-3 text-right">OEE %</th>
            <th className="pb-2 pr-3 text-right">Cap. após OEE</th>
            {DEDUCOES.map(({ campo, titulo }) => (
              <th key={campo} className="pb-2 pr-3 text-right">
                {titulo}
              </th>
            ))}
            <th className="pb-2 pr-3 text-right">Horas extra</th>
            <th className="pb-2 pr-3 text-left">Regime</th>
            <th className="pb-2 pr-3 text-right">Capacidade</th>
          </tr>
        </thead>
        <tbody>
          {resources.map((r) => (
            <LinhaCapacidade
              key={r.id}
              resource={r}
              efetivo={efetivo(r)}
              turnos={turnos}
              onEdit={(campo, valor) => onEdit(r.id, campo, valor)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CardRecursosFisicos: FC<{ fisicos: SeopPhysicalResource[] }> = ({ fisicos }) => (
  <div className="mt-4 rounded-xl border border-theme-border bg-surface-card p-5">
    <h3 className="mb-3 font-semibold text-content-primary">Recursos físicos cadastrados</h3>
    {fisicos.length === 0 ? (
      <p className="text-sm text-content-muted">
        Nenhuma máquina cadastrada — o cadastro fica na aba Cadastros.
      </p>
    ) : (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {fisicos.map((u) => (
          <div key={u.id} className="rounded-lg border border-theme-border bg-surface-secondary p-3">
            <div className="text-xs text-content-muted">{u.resource_group || u.critical}</div>
            <div className="text-sm font-medium text-content-primary">{u.name}</div>
            <div className="text-xs text-content-muted">
              Disponibilidade base: {fmtUtil(u.availability ?? 0)}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const SeopCapacityPage: FC = () => {
  const { month } = useSeopMonth();
  const { showToast } = useToast();
  const [resources, setResources] = useState<SeopResource[]>([]);
  const [capacities, setCapacities] = useState<SeopCapacity[]>([]);
  const [months, setMonths] = useState<SeopMonth[]>([]);
  const [levers, setLevers] = useState<SeopLever[]>([]);
  const [fisicos, setFisicos] = useState<SeopPhysicalResource[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Rascunho>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmando, setConfirmando] = useState<'replicar' | 'mes' | null>(null);
  const [avisoMes, setAvisoMes] = useState<string | null>(null);

  const mesAtual = months.find((m) => m.index === month) ?? null;
  const posteriores = months.filter((m) => m.index > month);
  const ultimoPosterior = posteriores[posteriores.length - 1];

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [rs, ms, lv, ph] = await Promise.all([
        seopService.getResources(),
        seopService.getMonths(),
        seopService.getLevers(),
        seopService.getPhysicalResources(),
      ]);
      setResources(rs);
      setMonths(ms);
      setLevers(lv);
      setFisicos(ph);
      const mes = ms.find((m) => m.index === month);
      setCapacities(mes ? await seopService.getCapacities(mes.id) : []);
      setDrafts({});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const porRecurso = useMemo(() => {
    const mapa = new Map<string, SeopCapacity>();
    capacities.forEach((c) => mapa.set(c.resource, c));
    return mapa;
  }, [capacities]);

  const dirty = Object.keys(drafts).length > 0;

  const salvar = useCallback(async () => {
    if (!mesAtual) {
      showToast('Sem mês cadastrado — use "Adicionar mês".', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const linhas: SeopCapacityInput[] = Object.entries(drafts).map(([resource, d]) => ({
        resource,
        month: mesAtual.id,
        ...d,
      }));
      await seopService.patchCapacityBatch(linhas);
      showToast('Capacidade salva.', 'success');
      await fetchData();
    } catch {
      showToast('Erro ao salvar a capacidade.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [drafts, mesAtual, showToast, fetchData]);

  useUnsavedChanges(dirty, salvar);

  const editar = (resource: string, campo: keyof Rascunho, valor: number | string) =>
    setDrafts((atual) => ({ ...atual, [resource]: { ...atual[resource], [campo]: valor } }));

  const efetivo = (r: SeopResource) => calcularEfetivo(porRecurso.get(r.id), drafts[r.id] ?? {});

  const replicar = async () => {
    if (!mesAtual) return;
    setIsSaving(true);
    try {
      const linhas: SeopCapacityInput[] = [];
      for (const destino of posteriores) {
        for (const r of resources) {
          const e = efetivo(r);
          linhas.push({
            resource: r.id,
            month: destino.id,
            // `calendar_days` NÃO é replicado de propósito: ele é do calendário do mês
            // (a janela 16→15 tem 30 ou 31 dias conforme o mês), e copiar o número de
            // agosto para setembro é exatamente a translação que o GUIA §12.3 marca como
            // origem de "divergência misteriosa". Deixado nulo, cada mês usa o seu.
            shift_hours: e.horasTurno,
            shift_count: e.turnos,
            oee: e.oee,
            flex_hours: e.extra,
            shift: e.shift,
          });
        }
      }
      await seopService.patchCapacityBatch(linhas);
      setConfirmando(null);
      showToast(
        `Capacidade replicada para ${posteriores.length} mês(es), até ${
          posteriores[posteriores.length - 1]?.label ?? ''
        }.`,
        'success',
      );
      await fetchData();
    } catch {
      showToast('Erro ao replicar a capacidade.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const adicionarMes = async () => {
    setIsSaving(true);
    try {
      const ultimo = months[months.length - 1];
      const proximoIndice = (ultimo?.index ?? -1) + 1;
      const base = ultimo ? new Date(`${ultimo.date}T00:00:00`) : new Date();
      base.setMonth(base.getMonth() + 1);
      const data = base.toISOString().slice(0, 10);
      const criado = await seopService.createMonth({
        date: data,
        index: proximoIndice,
        label: `M${proximoIndice + 1}`,
      });

      const origem = ultimo ? await seopService.getCapacities(ultimo.id) : [];
      const porId = new Map(origem.map((c) => [c.resource, c]));
      let herdaramPadrao = 0;
      const linhas: SeopCapacityInput[] = resources.map((r) => {
        const c = porId.get(r.id);
        const disp = c?.availability ? c.availability : DISPONIBILIDADE_PADRAO;
        if (!c?.availability) herdaramPadrao += 1;
        return {
          resource: r.id,
          month: criado.id,
          base_hours: c?.base_hours ?? 0,
          availability: disp,
          flex_hours: c?.flex_hours ?? 0,
          shift: c?.shift ?? '',
        };
      });
      if (linhas.length) await seopService.patchCapacityBatch(linhas);

      setConfirmando(null);
      setAvisoMes(
        herdaramPadrao > 0
          ? `${criado.label} criado. ${herdaramPadrao} recurso(s) sem disponibilidade no mês de origem receberam ${fmtUtil(
              DISPONIBILIDADE_PADRAO,
            )} — confira antes de planejar.`
          : `${criado.label} criado, herdando a capacidade do mês anterior.`,
      );
      showToast('Mês adicionado.', 'success');
      await fetchData();
    } catch {
      showToast('Erro ao adicionar o mês.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="p-6 lg:p-10 text-content-muted">Carregando a capacidade…</div>;

  if (error) {
    return (
      <div className="p-6 lg:p-10">
        <div className="rounded-lg border border-theme-danger/40 bg-theme-danger/10 p-4 text-content-primary">
          Não foi possível carregar a capacidade. {error}
        </div>
      </div>
    );
  }

  const turnos = turnosDisponiveis(levers);
  const tituloReplicar = posteriores.length ? undefined : 'Não há meses posteriores no horizonte';

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-sm text-content-muted">
          Capacidade = dias do calendário × horas de turno × turnos × OEE − deduções.
          As deduções entram em h:mm, depois do OEE.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="seop-adicionar-mes"
            onClick={() => setConfirmando('mes')}
            disabled={isSaving}
            className="rounded-lg border border-theme-border px-3 py-2 text-sm text-content-secondary hover:text-content-primary disabled:opacity-50"
          >
            Adicionar mês
          </button>
          <button
            type="button"
            data-testid="seop-replicar"
            onClick={() => setConfirmando('replicar')}
            disabled={isSaving || posteriores.length === 0}
            title={tituloReplicar}
            className="rounded-lg border border-theme-border px-3 py-2 text-sm text-content-secondary hover:text-content-primary disabled:opacity-50"
          >
            Replicar para meses seguintes
          </button>
          <button
            type="button"
            data-testid="seop-capacidade-salvar"
            onClick={salvar}
            disabled={!dirty || isSaving}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-accent-contrast hover:bg-accent-hover disabled:opacity-50"
          >
            {isSaving ? 'Salvando…' : 'Salvar alterações'}
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-lg border border-theme-border bg-surface-secondary px-4 py-3 text-sm text-content-secondary">
        Campos de horas usam o formato <strong className="text-content-primary">hh:mm</strong>. Exemplo:
        <strong className="text-content-primary"> 10:30</strong> equivale a dez horas e meia (também
        aceita 10,5).
      </div>

      {avisoMes && (
        <div
          data-testid="seop-aviso-mes"
          className="mb-4 rounded-lg border border-theme-warning/40 bg-theme-warning/10 px-4 py-3 text-sm text-content-primary"
        >
          {avisoMes}
        </div>
      )}

      <div className="rounded-xl border border-theme-border bg-surface-card p-5">
        <TabelaCapacidade
          resources={resources}
          efetivo={efetivo}
          turnos={turnos}
          onEdit={editar}
        />
      </div>

      <CardRecursosFisicos fisicos={fisicos} />

      <ConfirmModal
        open={confirmando === 'replicar'}
        title="Replicar a capacidade deste mês?"
        confirmLabel="Replicar"
        tone="accent"
        isBusy={isSaving}
        onConfirm={replicar}
        onCancel={() => setConfirmando(null)}
      >
        <p>
          A capacidade do mês atual será copiada para <strong>{posteriores.length} mês(es)</strong>{' '}
          seguinte(s), até {ultimoPosterior?.label ?? '—'}.
        </p>
        <p>Os valores já cadastrados nesses meses serão sobrescritos.</p>
      </ConfirmModal>

      <ConfirmModal
        open={confirmando === 'mes'}
        title="Adicionar um mês ao horizonte?"
        confirmLabel="Adicionar"
        tone="accent"
        isBusy={isSaving}
        onConfirm={adicionarMes}
        onCancel={() => setConfirmando(null)}
      >
        <p>
          O mês entra <strong>no fim</strong> do horizonte, herdando a capacidade do último mês e com
          demanda zerada.
        </p>
        <p>
          Recurso sem disponibilidade no mês de origem recebe{' '}
          <strong>{fmtUtil(DISPONIBILIDADE_PADRAO)}</strong>, e a tela avisa quantos foram.
        </p>
      </ConfirmModal>
    </div>
  );
};

export default SeopCapacityPage;
